/**
 * Single source of truth for the phone-number rule (raw digits, no
 * formatting). The client's tel input stores raw digits and requires a
 * COMPLETE number at submit; the server tolerates shorter DRAFT values on
 * incremental profile saves but rejects formatting chars, letters, or
 * overlong values on every save path. Change the digit count here and both
 * sides move together.
 */

export const PHONE_DIGITS = 10;

/** A complete phone number — what a form submit must contain. */
export const PHONE_COMPLETE_REGEX = new RegExp(`^\\d{${PHONE_DIGITS}}$`);

/** A structurally valid draft — digits only, up to the complete length. */
export const PHONE_DRAFT_REGEX = new RegExp(`^\\d{0,${PHONE_DIGITS}}$`);

/**
 * Normalizes a stored phone value for the raw-digit tel inputs (C30 — was
 * inlined in FirmProfile.tsx). Legacy values may be formatted
 * ("(555) 123-4567"): strip to digits so the prefill passes the shared
 * complete-number rule, dropping a leading "1" country code on 11-digit
 * values. Anything that still isn't a plausible number is returned as-is and
 * surfaces as a validation error the user must fix before changing the field.
 */
export const normalizePhone = (raw?: string): string => {
  if (!raw) return "";
  const digits = raw.replace(/\D/g, "");
  const trimmed =
    digits.length === PHONE_DIGITS + 1 && digits.startsWith("1") ? digits.slice(1) : digits;
  return PHONE_DRAFT_REGEX.test(trimmed) ? trimmed : raw;
};
